import { getLanguageCodeByLocale } from 'common/enums/LanguageCodeEnum'
import StyleWrapper from 'publisher/components/core/StyleWrapper'
import FieldErrors from 'publisher/components/FieldErrors'
import { typedPageSelectors, useOptIn, usePage } from 'publisher/store'
import { getAvailableCountries } from 'publisher/store/opt-in/optInSelectors'
import { getCustomerCountry } from 'publisher/store/optIn/optInSelectors'
import getCountries from 'publisher/utils/getCountries'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { useCalculateFormFieldStyles } from 'src/publisher/shared/hooks/use-calculate-form-fields-styles'
import { SelectNew } from 'src/shared/components/select-new'
import { ContactUsFieldEntityInterface } from 'src/shared/entities/contact-us-field/contact-us-field-entity.schema'
import { FieldEntityInterface } from 'src/shared/entities/field/field-entity.schema'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { CommonFieldSubEntityInterface } from 'src/shared/schemas/field/common-field-sub-entity.schema'

type CommonPublisherCountryFieldEntityOrSubEntityProps = {
  entityOrSubEntity:
    | FieldEntityInterface
    | ContactUsFieldEntityInterface
    | CommonFieldSubEntityInterface
  value: string | undefined
  onChange: (value: string) => void
  errors: string[]
}

export const CommonPublisherCountryFieldEntityOrSubEntity = ({
  entityOrSubEntity,
  value,
  onChange,
  errors,
}: CommonPublisherCountryFieldEntityOrSubEntityProps) => {
  const customerCountry = useOptIn(getCustomerCountry) || undefined
  const [countries, setCountries] = useState<Record<string, string>[]>([])
  const locale = usePage(state => typedPageSelectors.getLocale(state))
  const availableCountries = useOptIn(state => getAvailableCountries(state))
  const availableCountriesCodes = Object.keys(availableCountries)
  const { t } = useTypedTranslation()

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value)
  }

  useEffect(() => {
    if (availableCountriesCodes.length === 0) {
      setCountries(getCountries(getLanguageCodeByLocale(locale)))
    } else {
      setCountries(
        Object.keys(availableCountries).map(code => ({
          code,
          name: availableCountries[code],
        })),
      )
      if (availableCountriesCodes.length === 1) {
        onChange(availableCountriesCodes[0])
      }
    }
  }, [locale])

  useEffect(() => {
    if (
      countries.length !== 0 &&
      !value &&
      customerCountry &&
      countries.some(({ code }) => code === customerCountry)
    ) {
      onChange(customerCountry)
    }
  }, [countries.length, customerCountry])

  const {
    calculatedBorder,
    calculatedPadding,
    calculatedFontSize,
    calculatedLineHeight,
    calculatedTextAlign,
    calculatedFontFamily,
    calculatedColor,
    calculatedBackgroundColor,
  } = useCalculateFormFieldStyles({
    border: entityOrSubEntity.border,
    mobileBorder: entityOrSubEntity.mobileBorder,
    padding: entityOrSubEntity.padding,
    mobilePadding: entityOrSubEntity.mobilePadding,
    fontSize: entityOrSubEntity.fontSize,
    mobileFontSize: entityOrSubEntity.mobileFontSize,
    lineHeight: entityOrSubEntity.lineHeight,
    mobileLineHeight: entityOrSubEntity.mobileLineHeight,
    textAlign: entityOrSubEntity.textAlign,
    mobileTextAlign: entityOrSubEntity.mobileTextAlign,
    color: entityOrSubEntity.color,
    mobileColor: entityOrSubEntity.mobileColor,
    backgroundColor: entityOrSubEntity.backgroundColor,
    mobileBackgroundColor: entityOrSubEntity.mobileBackgroundColor,
  })

  return (
    <StyleWrapper
      margin={entityOrSubEntity.margin}
      mobileMargin={entityOrSubEntity.mobileMargin}
      appearance={entityOrSubEntity.appearance}
    >
      <SelectNew
        id={entityOrSubEntity.htmlAttrId}
        value={value}
        onChange={handleChange}
        fontFamily={calculatedFontFamily.desktop}
        mobileFontFamily={calculatedFontFamily.mobile}
        padding={calculatedPadding.desktop}
        mobilePadding={calculatedPadding.mobile}
        textAlign={calculatedTextAlign.desktop}
        mobileTextAlign={calculatedTextAlign.mobile}
        lineHeight={calculatedLineHeight.desktop}
        mobileLineHeight={calculatedLineHeight.mobile}
        border={calculatedBorder.desktop}
        mobileBorder={calculatedBorder.mobile}
        backgroundColor={calculatedBackgroundColor.desktop}
        mobileBackgroundColor={calculatedBackgroundColor.mobile}
        color={calculatedColor.desktop}
        mobileColor={calculatedColor.mobile}
        fontSize={calculatedFontSize.desktop}
        mobileFontSize={calculatedFontSize.mobile}
        boxShadow={entityOrSubEntity.boxShadow}
        mobileBoxShadow={entityOrSubEntity.mobileBoxShadow}
      >
        <option value="">{t('entities.field.country.placeholder')}</option>
        {countries.map(({ code, name }) => (
          <option key={code} value={code}>
            {name}
          </option>
        ))}
      </SelectNew>
      <FieldErrors errors={errors} />
    </StyleWrapper>
  )
}
